import { Box, Stack, Checkbox } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import SearchBar from "components/SearchBar";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { MessageTemplate } from "features/Resources/MessageTemplate/models";
import { useMemo, useState } from "react";
import { useMessageTemplateListQuery } from "features/Resources/MessageTemplate/hooks";
import { DisplayRecipients } from "features/Resources/MessageTemplate/Components/DisplayRecipients";
import { InternalLink } from "components/Link";
import { Text12 } from "components/Text";
import { createQueryString } from "helpers/queryString";
import { messageTemplatePathById } from "helpers/url/pages/v2/actionPaths";
import TemplateListEmptyState from "features/Resources/SharedTemplate/Components/TemplateListEmptyState";
import { TemplateTableDeleteButton } from "features/Resources/SharedTemplate/Components/TemplateTableButtons";
import { DisplayTemplatePaths } from "features/Resources/SharedTemplate/Components/DisplayTemplatePaths";
import { useCurrentUserQuery } from "features/User/hooks";
import SenderTag from "components/Tag/ResourceTargetTag/SenderTag";
import TriggerTiming from "components/Display/TriggerTiming";
import { ActionTypeTag } from "design-system";

const columnHelper = createColumnHelper<MessageTemplate>();

const columns = [
  columnHelper.accessor((row) => row, {
    id: "type",
    header: "",
    cell: () => <ActionTypeTag.IconBadge actionType="message" />,
    maxSize: 43,
    minSize: 43,
    size: 43,
    enableSorting: false,
    meta: {
      sx: {
        paddingLeft: "24px !important",
        paddingRight: "0 !important",
        width: "56px !important",
        maxWidth: "56px !important",
        minWidth: "56px !important",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    enableSorting: false,
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={messageTemplatePathById(row.getValue().id)}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
          width="fit-content"
        >
          <Text12 wordBreak="break-word" fontWeight="bold">
            {row.getValue()?.name}
          </Text12>
        </InternalLink>
      </Box>
    ),
    meta: {
      sx: {
        paddingLeft: "0 !important",
      },
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "recipients",
    header: "Recipient",
    enableSorting: false,
    cell: (row) => <DisplayRecipients messageTemplate={row.getValue()} />,
    meta: {
      sx: {
        width: "72px",
        maxWidth: "72px",
        minWidth: "72px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "sender",
    header: "Sender",
    enableSorting: false,
    cell: (row) => <SenderTag senderId={row.getValue().senderId} />,
    meta: {
      sx: {
        width: "72px",
        maxWidth: "72px",
        minWidth: "72px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "scheduled",
    header: "Deliver",
    enableSorting: false,
    cell: (row) => <TriggerTiming descriptor={row.getValue().deliverAtTrigger?.data?.descriptor} />,
    meta: {
      sx: {
        width: "160px",
        maxWidth: "160px",
        minWidth: "160px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "pathIds",
    header: "Paths",
    enableSorting: false,
    cell: (row) => <DisplayTemplatePaths template={row.getValue()} />,
    meta: {
      sx: {
        width: "100px",
        maxWidth: "100px",
        minWidth: "100px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "buttons",
    header: "",
    enableSorting: false,
    cell: (row) => (
      <TemplateTableDeleteButton templateSummary={{ templateType: "message_template", template: row.getValue() }} />
    ),
    meta: {
      sx: {
        width: "60px",
        maxWidth: "60px",
        minWidth: "60px",
      },
      headerHStackProps: { justifyContent: "flex-end" },
      cellHStackProps: { justifyContent: "flex-end" },
    },
  }),
];

export default function MessageTemplates() {
  const { data: currentUser } = useCurrentUserQuery();
  const [searchText, setSearchText] = useState("");
  const [showMyTemplatesOnly, setShowMyTemplatesOnly] = useState(false);

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: 200 },
        additionalQueries: {
          q: searchText && searchText.length > 2 ? searchText : undefined,
        },
      }),
    [searchText],
  );

  const { data } = useMessageTemplateListQuery(queryString);

  // Filter templates to show only user's templates if checkbox is checked
  const filteredData = useMemo(() => {
    if (!data) return [];
    const filtered =
      showMyTemplatesOnly && currentUser ? data.filter((template) => template.senderId === currentUser.id) : data;
    return [...filtered].sort(
      (a, b) => (a.deliverAtTrigger?.sortValue ?? Infinity) - (b.deliverAtTrigger?.sortValue ?? Infinity),
    );
  }, [data, showMyTemplatesOnly, currentUser]);

  return (
    <Stack direction="column" gap="4" w="100%">
      <Stack direction="row" w="100%" alignItems="center" justifyContent="space-between" gap="2">
        <SearchBar minWidth="500" onInput={setSearchText} />
        <Checkbox isChecked={showMyTemplatesOnly} onChange={(e) => setShowMyTemplatesOnly(e.target.checked)}>
          Show only my message templates
        </Checkbox>
      </Stack>

      <ControlledDataTable<MessageTemplate>
        data={filteredData}
        columns={columns}
        initialPagination={{ pageSize: 200, pageIndex: 0 }}
        emptyStateComponent={
          <TemplateListEmptyState
            actionType="message"
            hasActiveFilters={searchText.length > 2 || showMyTemplatesOnly}
          />
        }
      />
    </Stack>
  );
}
